import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView, useMotionValueEvent } from "framer-motion";
import { operationsIntro, integrationChart, integrationBrands, moneySignals, behaviorSignals } from "../../data/reportData";
import { CountUp } from "../charts/Charts";
import { CircledWord } from "../CircledWord";

const fadeUp = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
};

function DotGrid({ value, dark, active }) {
  const ref = useRef(null);
  const inV = useInView(ref, { margin: "-40px" });
  const controlled = active !== undefined;
  const on = controlled ? active && inV : inV;
  const inactive = dark ? "#FFFFFF" : "#E4E4E4";

  return (
    <div ref={ref} className="grid grid-cols-10 gap-[14px] md:gap-[19px] w-fit" aria-label={`${value} out of 100`}>
      {Array.from({ length: 100 }).map((_, i) => {
        const row = Math.floor(i / 10);
        const fromBottom = (9 - row) * 10 + (i % 10);
        const filled = fromBottom < value;
        if (!filled) {
          return <span key={i} className="w-[17px] h-[17px] md:w-[21px] md:h-[21px] rounded-full" style={{ background: inactive }} />;
        }
        return (
          <motion.span
            key={i}
            className="w-[17px] h-[17px] md:w-[21px] md:h-[21px] rounded-full"
            initial={false}
            animate={on ? { backgroundColor: "#53B851" } : { backgroundColor: inactive }}
            transition={on ? { delay: fromBottom * 0.03, duration: 0.25 } : { duration: 0.2 }}
            style={{ background: inactive }}
          />
        );
      })}
    </div>
  );
}

function SignalCard({ s, dark, active }) {
  return (
    <div
      className={`w-[605px] max-w-[86vw] shrink-0 p-7 md:p-9 flex flex-col ${dark ? "bg-[#0A0A0A]" : "bg-white border border-[#D9D9D9]"}`}
      data-testid={`signal-card-${s.label.replace(/\s+/g, "-")}`}
    >
      <div className="font-display font-medium text-[36px] md:text-[44px] leading-none tracking-[-0.02em]" style={{ color: "#53B851" }}>
        {s.fraction}
      </div>
      <p className={`text-[15px] md:text-[15.5px] mt-5 ${dark ? "text-white" : "text-[#0A0A0A]"}`}>{s.label}</p>
      <p className={`text-[14px] md:text-[14.5px] mt-1 leading-relaxed ${dark ? "text-[#C9C9C9]" : "text-[#8A8A8A]"}`}>{s.sub}</p>
      <div className="my-8 md:my-10 self-center">
        <DotGrid value={s.value} dark={dark} active={active} />
      </div>
      <p className="text-[12px] md:text-[12.5px] tracking-[0.08em] uppercase mt-auto">
        <span className="font-semibold" style={{ color: "#53B851" }}>{s.value}%</span>
        <span className={dark ? "text-[#9C9C9C]" : "text-[#8A8A86]"}>{s.unit.replace(/^\d+%/, "")}</span>
      </p>
    </div>
  );
}

// scroll-pinned pair: right card comes into focus, left card exits to a mirrored position
function SignalPair({ items, testId }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0.08, 0.92], ["0%", "-57.1%"]);
  const [focusIdx, setFocusIdx] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => setFocusIdx(v > 0.5 ? 1 : 0));

  return (
    <>
      <div ref={ref} className="hidden md:block h-[240vh]" data-testid={testId}>
        <div className="sticky top-0 h-screen flex items-center overflow-hidden">
          <div className="report-col w-full">
            <motion.div className="flex gap-[200px] w-max" style={{ x }}>
              <SignalCard s={items[0]} active={focusIdx === 0} />
              <SignalCard s={items[1]} dark active={focusIdx === 1} />
            </motion.div>
          </div>
        </div>
      </div>
      {/* mobile: static stacked */}
      <div className="md:hidden report-col space-y-5 mt-10" data-testid={`${testId}-mobile`}>
        <SignalCard s={items[0]} />
        <SignalCard s={items[1]} dark />
      </div>
    </>
  );
}

export default function OperationsSection() {
  return (
    <section id="operations" data-testid="section-operations">
      <div className="bg-black py-16 md:py-24" data-testid="operations-dark">
        <div className="report-col">
          <motion.p
            {...fadeUp}
            className="uppercase text-[12.5px] tracking-[0.2em] font-medium mb-5"
            style={{ color: "#53B851" }}
          >
            Running on it
          </motion.p>
          <motion.h2 {...fadeUp} className="section-h2" style={{ color: "#FFFFFF" }}>
            These apps became a part of the <CircledWord color="#53B851">business</CircledWord>
          </motion.h2>
          <div className="space-y-6 mt-8">
            {operationsIntro.map((p, i) => (
              <motion.p key={i} {...fadeUp} className="body-text" style={{ color: "#C4C4C4", maxWidth: 800 }}>{p}</motion.p>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-12 md:mt-16" data-testid="integration-explainer">
            <p className="uppercase text-[16px] tracking-[0.18em] font-medium mb-3" style={{ color: "#53B851" }}>
              What counts as an integration
            </p>
            <p className="text-[16px] leading-[1.7] max-w-2xl" style={{ color: "#C4C4C4" }}>
              An integration is an outside service wired into the app: the payment rail that takes the money, the email that sends the receipt, the maps layer that finds the customer. These are the brands operators wire in most.
            </p>
          </motion.div>
        </div>

        {/* full-bleed logo marquee */}
        <motion.div {...fadeUp} className="overflow-hidden mt-10 md:mt-14" data-testid="integration-brands">
          <div className="brands-track">
            {[0, 1].map((dup) => (
              <div key={dup} className="flex gap-5 md:gap-6 pr-5 md:pr-6" aria-hidden={dup === 1}>
                {integrationBrands.map((b) => (
                  <div
                    key={`${dup}-${b.name}`}
                    className="w-[140px] h-[140px] md:w-[180px] md:h-[180px] bg-white flex flex-col items-center justify-center gap-3 shrink-0"
                    data-testid={dup === 0 ? `brand-tile-${b.name.replace(/\s+/g, "-")}` : undefined}
                  >
                    {(b.icon || b.slug) && (
                      <img
                        src={b.icon || `https://cdn.simpleicons.org/${b.slug}`}
                        alt={b.name}
                        className="w-11 h-11 md:w-14 md:h-14 object-contain"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    )}
                    <span className="text-[11.5px] md:text-[12.5px] text-[#3D3D3B] text-center leading-tight px-2">{b.name}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </motion.div>

        {/* integration depth: two cumulative fill bars */}
        <div className="report-col">
          <motion.figure {...fadeUp} className="mt-16 md:mt-28" data-testid="integration-chart">
            <figcaption className="mb-10">
              <span className="font-display font-medium text-[22px] md:text-[26px] tracking-[-0.01em] block" style={{ color: "#FFFFFF" }}>
                {integrationChart.title}
              </span>
              <span className="text-[14px] md:text-[15px] leading-relaxed block mt-2 max-w-2xl" style={{ color: "#9C9C9C" }}>
                {integrationChart.subtitle}
              </span>
            </figcaption>
            <div className="relative">
              <div className="relative space-y-12">
                {integrationChart.rows.map((row, i) => (
                  <motion.div
                    key={row.value}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ delay: i * 0.15, duration: 0.6 }}
                    data-testid={`integration-row-${i}`}
                  >
                    <div className="flex items-end justify-between gap-6 mb-4">
                      <div className="font-display font-semibold text-[48px] md:text-[58px] leading-none tracking-[-0.03em]" style={{ color: "#53B851" }}>
                        <CountUp value={row.value} suffix="%" duration={1.6} />
                      </div>
                      <p className="text-[14.5px] md:text-[15px] leading-snug max-w-md text-right pb-1" style={{ color: "#E6E6E6" }}>{row.label}</p>
                    </div>
                    <div className="relative h-[26px] md:h-[28px] bg-[#4F4F4F] overflow-hidden">
                      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                        {Array.from({ length: 11 }).map((_, gi) => (
                          <span key={gi} className="absolute inset-y-0 w-px" style={{ left: `${gi * 10}%`, background: "rgba(255,255,255,0.1)" }} />
                        ))}
                      </div>
                      <motion.div
                        className="relative h-full"
                        style={{ background: "#6FCD7D", transformOrigin: "left" }}
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: row.value / 100 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ delay: 0.2 + i * 0.15, duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
            <p className="text-[14px] md:text-[15px] mt-9" style={{ color: "#B3B3B3" }}>
              Source: <span className="font-semibold" style={{ color: "#53B851" }}>Emergent</span> platform data, deployed-app cohort
            </p>
          </motion.figure>
        </div>
      </div>

      <div className="pt-14 md:pt-20">
        <div className="report-col">
          <motion.p {...fadeUp} className="body-text">
            The <span className="font-semibold" style={{ color: "#53B851" }}>connections</span> land where money comes in and customers are served:
          </motion.p>
        </div>

        <SignalPair items={moneySignals} testId="money-signals" />

        <div className="report-col">
          <motion.div {...fadeUp} className="mt-12 md:mt-0 pl-6 md:pl-8 py-1" style={{ borderLeft: "4px solid #53B851" }} data-testid="operations-pull">
            <p className="font-display font-medium text-[24px] md:text-[28px] leading-[1.3] tracking-[-0.01em] text-[#0A0A0A]">
              You do not wire a checkout or an automated receipt into something you are experimenting with.
            </p>
          </motion.div>

          <motion.p {...fadeUp} className="body-text mt-12 md:mt-16">
            Two <span className="font-semibold" style={{ color: "#53B851" }}>behaviors</span> outside the codebase say the same thing:
          </motion.p>
        </div>

        <SignalPair items={behaviorSignals} testId="behavior-signals" />

        <div className="report-col">
          <motion.div {...fadeUp} className="mt-12 md:mt-0 pl-6 md:pl-8 py-1" style={{ borderLeft: "4px solid #53B851" }} data-testid="operations-pull-2">
            <p className="font-display font-medium text-[24px] md:text-[28px] leading-[1.3] tracking-[-0.01em] text-[#0A0A0A]">
              A prototype rarely earns its own domain. Neither does it get a second month of development.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
